"use client"

import Title from "@/components/common/Typography/Title"
import { Col, Row } from "antd"
import Image from "next/image"
import FormSearchFlightMobile from "./FormSearchFlightMobile"
import { useIsDesktop } from "@/hooks/app/useIsDesktop"
import PopupSelectPassenger, { PopupSelectPassengerRef } from "./PopupSelectPassenger"
import PopupSelectClass from "./PopupSelectClass"
import DrawerSelectPlacement, { DrawerSelectPlacementRef } from "./DrawerSelectPlacement"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import { flightActions } from "@/lib/redux/features/flight/reduce"
import DrawerSelectDate, { DrawerSelectDateRef } from "@/components/common/DrawerSelectDate"

import { DrawerFlightDetailRef } from "../../flight-detail/mobile/DrawerFlightDetail"
import PageHeaderMobile from "@/components/common/PageHeaderMobile"
import { useAirports } from "@/hooks/flight/useAirports"
import { getMinFlights } from "@/utils/flight/getMinFlights"
import { useTERouter } from "@/hooks/app/useTERouter"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { useTranslation } from "react-i18next"
import { FlightSearchPrefill } from "../../flights/landing/flightSearchPrefill"
import { useEffect, useRef } from "react"
import dayjs from "dayjs"

export const searchFlightDrawers: {
  flights: any,
  flightDetail: DrawerFlightDetailRef | null,
  selectPassenger: PopupSelectPassengerRef | null,
  selectPlacementTo: DrawerSelectPlacementRef | null,
  selectPlacementFrom: DrawerSelectPlacementRef | null,
  selectClass: DrawerSelectPlacementRef | null,
  selectDate: DrawerSelectDateRef | null,
} = {
  flights: null,
  selectPassenger: null,
  selectPlacementTo: null,
  selectPlacementFrom: null,
  selectClass: null,
  flightDetail: null,
  selectDate: null,
}

const DrawerFormSearchFlightWrapper = ({ children }: { children?: any }) => {
  const router = useTERouter()
  useAirports()
  const dispatch = useAppDispatch()
  const searchMin = useAppSelector(state => Object.values(state.flight.minFlightsByMonths).map(obj => {
    const values = Object.values(obj)
    return values.reduce((obj1, fare) => {
      return {
        ...obj1,
        [fare.date]: `${Math.floor(fare.lowestFare / 1000)}k`
      }
    }, {})
  }).reduce((obj, minFares) => ({ ...obj, ...minFares }), {}))
  const selectedFligthInputIndex = useAppSelector(state => state.flight.selectedFligthInputIndex)
  const selectedInputFlight = useAppSelector(state => state.flight.inputFlights[state.flight.selectedFligthInputIndex])
  const checkedItinerary = useAppSelector(state => state.flight.checkedItinerary)
  const dateFrom = selectedInputFlight?.dateFrom
  const dateTo = selectedInputFlight?.dateTo
  const searchingFrom = useAppSelector(state => state.flight.searchingFrom)
  const searchingTo = useAppSelector(state => state.flight.searchingTo)
  const { getValidDayjs } = useTETranslation()
  const { t } = useTranslation()

  return (
    <>
      <PopupSelectPassenger ref={(curr) => { searchFlightDrawers.selectPassenger = curr; }} />
      <PopupSelectClass ref={(curr) => { searchFlightDrawers.selectClass = curr; }} />
      <DrawerSelectPlacement
        mode="to"
        value={selectedInputFlight?.toAirport}
        onChange={(airport) => {
          dispatch(flightActions.resetResultsFlight())
          setTimeout(() => {
            getMinFlights(dateFrom)
          }, 200)
          dispatch(flightActions.selectToAirport({ indexInputFlight: selectedFligthInputIndex, toAirport: airport }))
          dispatch(flightActions.searchAirport({
            value: "",
            searchPopular: true
          }))
        }}
        chooseCurrentLy={(from, to) => {
          dispatch(flightActions.selectToAirport({ indexInputFlight: selectedFligthInputIndex, toAirport: to }))
          dispatch(flightActions.selectFromAirport({ indexInputFlight: selectedFligthInputIndex, fromAirport: from }))
        }}
        onSearch={(val) => {
          if (val) {
            if (!searchingTo) {
              dispatch(flightActions.updateSearchingTo(true))
            }
          } else {
            if (searchingTo) {
              dispatch(flightActions.updateSearchingTo(false))
            }
          }
        }}
        ref={(curr) => { searchFlightDrawers.selectPlacementTo = curr; }}
        searching={searchingTo}
      />
      <DrawerSelectPlacement
        mode="from"
        value={selectedInputFlight?.fromAirport}
        onChange={(airport) => {
          dispatch(flightActions.resetResultsFlight())
          setTimeout(() => {
            getMinFlights(dateFrom)
          }, 200)
          dispatch(flightActions.selectFromAirport({ indexInputFlight: selectedFligthInputIndex, fromAirport: airport }))
          dispatch(flightActions.searchAirport({
            value: "",
            searchPopular: true
          }))
          // After picking departure, open destination picker
          setTimeout(() => {
            searchFlightDrawers.selectPlacementTo?.open()
          }, 280)
        }}
        chooseCurrentLy={(from, to) => {
          dispatch(flightActions.selectToAirport({ indexInputFlight: selectedFligthInputIndex, toAirport: to }))
          dispatch(flightActions.selectFromAirport({ indexInputFlight: selectedFligthInputIndex, fromAirport: from }))
        }}
        onSearch={(val) => {
          if (val) {
            if (!searchingFrom) {
              dispatch(flightActions.updateSearchingFrom(true))
            }
          } else {
            if (searchingFrom) {
              dispatch(flightActions.updateSearchingFrom(false))
            }
          }
        }}
        ref={(curr) => { searchFlightDrawers.selectPlacementFrom = curr; }}
        searching={searchingFrom}
      />
      <DrawerSelectDate
        variant="flight"
        value={[getValidDayjs(dateFrom), getValidDayjs(dateTo)]}
        range={checkedItinerary}
        ref={(curr) => { searchFlightDrawers.selectDate = curr; }}
        onCalendarChange={(dateStr) => {
          getMinFlights(dateStr)
        }}
        subData={searchMin}
        onChange={([from, to]) => {
          dispatch(flightActions.changeDateFrom({ indexInputFlight: selectedFligthInputIndex, dateFrom: from }))
          if (to) {
            // Validate: if dateFrom > dateTo, adjust dateTo to dateFrom + 1 day
            const fromDayjs = getValidDayjs(from)
            const toDayjs = getValidDayjs(to)
            const validTo = fromDayjs.isAfter(toDayjs) || fromDayjs.isSame(toDayjs, 'day')
              ? fromDayjs.add(1, 'day').format('YYYY-MM-DD')
              : to
            dispatch(flightActions.changeDateTo({ indexInputFlight: selectedFligthInputIndex, dateTo: validTo }))
            dispatch(flightActions.changeCheckedItinerary(true))
          } else if (from && dateTo) {
            // When only start date changed, validate against existing dateTo
            const fromDayjs = getValidDayjs(from)
            const toDayjs = getValidDayjs(dateTo)
            if (fromDayjs.isAfter(toDayjs) || fromDayjs.isSame(toDayjs, 'day')) {
              dispatch(flightActions.changeDateTo({ indexInputFlight: selectedFligthInputIndex, dateTo: fromDayjs.add(1, 'day').format('YYYY-MM-DD') }))
            }
          }
        }}
      />
      {children}
    </>
  )
}

export default DrawerFormSearchFlightWrapper